import { Injectable } from "@angular/core"
import { HttpHeaders } from "@angular/common/http"
import { Observable, BehaviorSubject, map } from "rxjs"
import { StationStoreService } from "./station-store.service"
import { environment } from "../../environments/environment"
import { ApiService } from "./api.service"
import { Station } from "../models/station.model"

export interface StationResponse {
  status: string
  stations: Station[]
  pagination: {
    total: number
    limit: number
    offset: number
    has_more: boolean
  }
  environment: string
}

export interface DayRatings {
  [hour: number]: number
}

// Only days of week → DayRatings
export type RatingsByDay = {
  mon: DayRatings
  tue: DayRatings
  wed: DayRatings
  thu: DayRatings
  fri: DayRatings
  sat: DayRatings
  sun: DayRatings
}

// Full ratings object with station info + days
export interface RatingsData extends RatingsByDay {
  stationid: number
  last_updated: string
}

export interface StationRatings {
  station: {
    id: number
    call_sign: string
    name: string
    dial_position: number
    format: string
  }
  ratings: RatingsData
}

export interface RatingsResponse {
  status: string
  data: StationRatings
  environment: string
}

export interface RatingUpdate {
  day: string
  hour: number
  rating: number
}

export interface ManualRatingUpdate {
  stationid?: number
  day: string
  hour: number
  rating: number
}

@Injectable({
  providedIn: "root",
})
export class RatingsService {
  private baseUrl = environment.apiUrl
  private selectedStationSubject = new BehaviorSubject<Station | null>(null)
  public selectedStation$ = this.selectedStationSubject.asObservable()

  constructor(
    private apiService: ApiService,
    private stationStore: StationStoreService,
  ) {}

  // Get all stations (centralized cache)
  getStations(): Observable<StationResponse> {
    return this.stationStore.getStationsListResponse().pipe(
      map((res) => ({
        status: "success",
        stations: res.stations,
        pagination: res.pagination,
        environment: "",
      })),
    )
  }

  // Get ratings for a specific station
  getStationRatings(stationId: number): Observable<RatingsResponse> {
    return this.apiService.get<RatingsResponse>(`${this.baseUrl}/ratingsInfo?stationId=${stationId}`)
  }

  // Create/Update ratings manually
  createRatings(updates: ManualRatingUpdate[]): Observable<any> {
    const payload = { updates }
    return this.apiService.post<any>(`${this.baseUrl}/ratingsInfo`, payload)
  }

  // Update existing ratings
  updateRatings(stationId: number, updates: RatingUpdate[]): Observable<any> {
    const payload = { updates }
    return this.apiService.put<any>(`${this.baseUrl}/ratingsInfo/${stationId}`, payload)
  }

  // Delete ratings for a station
  deleteRatings(stationId: number): Observable<any> {
    return this.apiService.delete<any>(`${this.baseUrl}/ratingsInfo/${stationId}`)
  }

  // Upload TAPSCAN file
  uploadTapscanFile(stationId: number, file: File, fileType: "MF" | "SS"): Observable<any> {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("stationid", stationId.toString())
    formData.append("fileType", fileType)

    const headers = new HttpHeaders()
    // Don't set Content-Type header, let browser set it with boundary for multipart

    return this.apiService.postFormData<any>(`${this.baseUrl}/ratingsInfo`, formData)
  }

  // Set selected station
  setSelectedStation(station: Station | null): void {
    this.selectedStationSubject.next(station)
  }

  // Get selected station
  getSelectedStation(): Station | null {
    return this.selectedStationSubject.value
  }

  // Helper method to get day names
  getDayNames(): string[] {
    return ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"]
  }

  // Helper method to get hour display
  getHourDisplay(hour: number): string {
    if (hour === 0) return "12:00 AM"
    if (hour === 12) return "12:00 PM"
    if (hour < 12) return `${hour}:00 AM`
    return `${hour - 12}:00 PM`
  }

  // Helper method to format ratings for display
  formatRating(rating: number): string {
    return rating.toLocaleString()
  }
}
